import { Layout } from '@/components/layouts/layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSession } from '@/lib/auth-client';
import {
    getActiveTenant,
    getActiveTenantId,
    isSubscriptionActive,
    updateTenantTaxRate,
} from '@/lib/tenant';
import { createFileRoute, useRouterState } from '@tanstack/react-router';
import { Pencil, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/account')({
    component: Account,
});

type PaymentMethod = {
    id: string;
    brand: string | null;
    last4: string | null;
    expMonth: number | null;
    expYear: number | null;
    name: string | null;
    isPrimary: boolean;
};

type Invoice = {
    id: string;
    number: string;
    description: string;
    status: string;
    date: string | null;
    amount: number;
    currency: string;
};

type BillingDetails = {
    subscription: {
        status: string;
        interval: string | null;
        amount: number | null;
        currency: string;
        currentPeriodEnd: string | null;
    } | null;
    paymentMethods: PaymentMethod[];
    invoices: Invoice[];
};

const MONTH_NAMES = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatCurrency(amount: number, currency: string) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
    }).format(amount / 100);
}

function InvoiceStatusBadge({ status }: { status: string }) {
    if (status === 'paid') {
        return (
            <Badge className="rounded-full border border-emerald-500/60 bg-transparent text-emerald-400">
                paid
            </Badge>
        );
    }
    if (status === 'open' || status === 'draft') {
        return (
            <Badge className="rounded-full border border-amber-500/60 bg-transparent text-amber-400">
                pending
            </Badge>
        );
    }
    if (status === 'uncollectible') {
        return (
            <Badge className="rounded-full border border-red-500/60 bg-transparent text-red-400">
                failed
            </Badge>
        );
    }
    return (
        <Badge className="rounded-full border border-border bg-transparent text-muted-foreground">
            {status}
        </Badge>
    );
}

function Account() {
    const { data: session } = useSession();
    const location = useRouterState({ select: (state) => state.location });
    const [billingStatus, setBillingStatus] = useState<string | null>(null);
    const [billingError, setBillingError] = useState<string | null>(null);
    const [isBillingLoading, setIsBillingLoading] = useState(false);
    const [isStatusChecking, setIsStatusChecking] = useState(false);
    const [isTenantLoading, setIsTenantLoading] = useState(false);
    const [accessCode, setAccessCode] = useState('');
    const [tenantError, setTenantError] = useState<string | null>(null);
    const [tenantSnapshot, setTenantSnapshot] = useState<Awaited<
        ReturnType<typeof getActiveTenant>
    > | null>(null);
    const [statusTone, setStatusTone] = useState<
        'success' | 'warning' | 'info' | null
    >(null);
    const [billingDetails, setBillingDetails] = useState<BillingDetails | null>(null);
    const [isBillingDetailsLoading, setIsBillingDetailsLoading] = useState(false);
    const [taxRateInput, setTaxRateInput] = useState('');
    const [isTaxSaving, setIsTaxSaving] = useState(false);

    const userName =
        (session?.user?.user_metadata?.full_name as string | undefined) ??
        'User';
    const userEmail = session?.user?.email ?? '—';
    const apiBase = (
        (import.meta.env.VITE_BILLING_API_BASE_URL as string | undefined) ??
        (import.meta.env.VITE_API_BASE_URL as string | undefined)
    )?.replace(/\/$/, '');

    const billingParam = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('billing');
    }, [location.search]);

    const subscriptionStatus = tenantSnapshot?.subscriptionStatus ?? null;
    const isActiveSubscription = isSubscriptionActive(subscriptionStatus);

    const checkSubscriptionStatus = async () => {
        setIsStatusChecking(true);
        setBillingError(null);
        setBillingStatus('Checking Stripe for your subscription...');
        setStatusTone('info');

        try {
            if (!session?.access_token) {
                throw new Error('You must be signed in to check billing.');
            }

            const tenantId = await getActiveTenantId();
            const syncResponse = await fetch(
                `${apiBase ?? ''}/api/stripe/sync`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({ tenantId }),
                },
            );

            const syncPayload = (await syncResponse.json()) as
                | { synced?: boolean; reason?: string; error?: string }
                | undefined;

            if (!syncResponse.ok) {
                throw new Error(
                    syncPayload?.error ?? 'Unable to sync with Stripe.',
                );
            }

            const tenant = await getActiveTenant({ force: true });
            setTenantSnapshot(tenant);
            if (isSubscriptionActive(tenant.subscriptionStatus)) {
                setBillingStatus('Subscription active.');
                setStatusTone('success');
                return;
            }

            setBillingStatus(
                'We have not received an active subscription yet. If you just paid, give it a moment and refresh.',
            );
            setStatusTone('warning');
        } catch (error) {
            setBillingError(
                error instanceof Error
                    ? error.message
                    : 'Unable to check subscription status.',
            );
            setBillingStatus(null);
            setStatusTone(null);
        } finally {
            setIsStatusChecking(false);
        }
    };

    const createBillingSession = async (endpoint: 'checkout' | 'portal') => {
        if (!session?.access_token) {
            setBillingError('You must be signed in to manage billing.');
            return;
        }

        setIsBillingLoading(true);
        setBillingError(null);
        setBillingStatus(null);

        try {
            const tenantId = await getActiveTenantId();
            const trimmedAccessCode = accessCode.trim();
            const response = await fetch(
                `${apiBase ?? ''}/api/stripe/${endpoint}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({
                        tenantId,
                        ...(endpoint === 'checkout' && trimmedAccessCode
                            ? { accessCode: trimmedAccessCode }
                            : {}),
                    }),
                },
            );

            const payload = (await response.json()) as
                | { url?: string; error?: string }
                | undefined;

            if (!response.ok) {
                throw new Error(payload?.error ?? 'Unable to start billing.');
            }

            if (payload?.url) {
                setBillingStatus('Redirecting to Stripe...');
                window.location.assign(payload.url);
                return;
            }

            throw new Error('Stripe did not return a checkout link.');
        } catch (error) {
            setBillingError(
                error instanceof Error
                    ? error.message
                    : 'Unable to start billing.',
            );
        } finally {
            setIsBillingLoading(false);
        }
    };

    const fetchBillingDetails = async () => {
        if (!session?.access_token) return;
        setIsBillingDetailsLoading(true);
        try {
            const tenantId = await getActiveTenantId();
            const response = await fetch(
                `${apiBase ?? ''}/api/stripe/billing-details`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({ tenantId }),
                },
            );
            if (!response.ok) return;
            const data = (await response.json()) as BillingDetails;
            setBillingDetails(data);
        } catch {
            // non-critical, subscription status still shows
        } finally {
            setIsBillingDetailsLoading(false);
        }
    };

    useEffect(() => {
        if (!billingParam || !session?.access_token) {
            return;
        }
        void checkSubscriptionStatus();
    }, [billingParam, session?.access_token]);

    useEffect(() => {
        let isMounted = true;
        const loadTenant = async () => {
            setIsTenantLoading(true);
            setTenantError(null);
            try {
                const tenant = await getActiveTenant();
                if (!isMounted) return;
                setTenantSnapshot(tenant);
                if (tenant.taxRatePct != null) {
                    setTaxRateInput(String(tenant.taxRatePct));
                }
            } catch (error) {
                if (!isMounted) return;
                setTenantError(
                    error instanceof Error
                        ? error.message
                        : 'Unable to load subscription details.',
                );
            } finally {
                if (isMounted) setIsTenantLoading(false);
            }
        };
        void loadTenant();
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        if (session?.access_token) {
            void fetchBillingDetails();
        }
    }, [session?.access_token]);

    const subDetails = billingDetails?.subscription;
    const billingIntervalLabel = subDetails?.interval
        ? `Billing ${subDetails.interval === 'year' ? 'annually' : 'monthly'}`
        : null;
    const nextPaymentLabel = useMemo(() => {
        if (!subDetails?.currentPeriodEnd || !subDetails?.amount) return null;
        const date = new Date(subDetails.currentPeriodEnd);
        if (Number.isNaN(date.getTime())) return null;
        const formatted = new Intl.DateTimeFormat('en-US', {
            dateStyle: 'short',
        }).format(date);
        const amount = formatCurrency(subDetails.amount, subDetails.currency);
        return `Next payment on ${formatted} for ${amount}`;
    }, [subDetails]);

    const fallbackSubscriptionLabel = useMemo(() => {
        if (!isActiveSubscription || (billingIntervalLabel ?? nextPaymentLabel)) {
            return null;
        }
        const statusMap: Record<string, string> = {
            active: 'Active',
            trialing: 'Free trial',
            past_due: 'Past due',
        };
        const statusLabel = subscriptionStatus
            ? (statusMap[subscriptionStatus] ?? subscriptionStatus.replace(/_/g, ' '))
            : null;
        const periodEnd = tenantSnapshot?.subscriptionCurrentPeriodEnd
            ? new Date(tenantSnapshot.subscriptionCurrentPeriodEnd)
            : null;
        const periodEndLabel =
            periodEnd && !Number.isNaN(periodEnd.getTime())
                ? new Intl.DateTimeFormat('en-US', { dateStyle: 'short' }).format(
                      periodEnd,
                  )
                : null;
        return [
            statusLabel,
            periodEndLabel
                ? `${subscriptionStatus === 'trialing' ? 'Trial ends' : 'Renews'} ${periodEndLabel}`
                : null,
        ]
            .filter(Boolean)
            .join(' · ');
    }, [isActiveSubscription, billingIntervalLabel, nextPaymentLabel, subscriptionStatus, tenantSnapshot]);

    const saveTaxRate = async () => {
        setIsTaxSaving(true);
        try {
            const tenantId = await getActiveTenantId();
            const parsed = taxRateInput.trim() === '' ? null : Number(taxRateInput);
            if (parsed !== null && (isNaN(parsed) || parsed < 0 || parsed > 100)) {
                toast.error('Enter a percentage between 0 and 100.');
                return;
            }
            await updateTenantTaxRate(tenantId, parsed);
            toast.success('Tax rate saved.');
        } catch {
            toast.error('Failed to save tax rate.');
        } finally {
            setIsTaxSaving(false);
        }
    };

    return (
        <Layout>
            <div className="mx-auto w-full max-w-3xl px-4 py-8">
                <h1 className="text-2xl font-semibold">Account</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    Manage your profile details and subscription.
                </p>

                {/* Profile card */}
                <div className="mt-6 rounded-lg border border-border bg-card p-4 shadow-sm">
                    <div className="text-sm text-muted-foreground">Name</div>
                    <div className="mt-1 text-base font-medium">{userName}</div>
                    <div className="mt-4 text-sm text-muted-foreground">Email</div>
                    <div className="mt-1 text-base font-medium">{userEmail}</div>
                </div>

                {/* Billing card */}
                <div className="mt-4 rounded-lg border border-border bg-card p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h2 className="text-base font-semibold">Billing</h2>
                            {isTenantLoading || isBillingDetailsLoading ? (
                                <p className="mt-1 text-sm text-muted-foreground">
                                    Loading…
                                </p>
                            ) : tenantError ? (
                                <p className="mt-1 text-sm text-destructive">
                                    {tenantError}
                                </p>
                            ) : (billingIntervalLabel ?? nextPaymentLabel) ? (
                                <p className="mt-1 text-sm text-muted-foreground">
                                    {[billingIntervalLabel, nextPaymentLabel]
                                        .filter(Boolean)
                                        .join(' | ')}
                                </p>
                            ) : fallbackSubscriptionLabel ? (
                                <p className="mt-1 text-sm text-muted-foreground">
                                    {fallbackSubscriptionLabel}
                                </p>
                            ) : (
                                <p className="mt-1 text-sm text-muted-foreground">
                                    No active subscription
                                </p>
                            )}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => createBillingSession('portal')}
                            disabled={isBillingLoading}
                        >
                            {isActiveSubscription ? 'Change plan' : 'Manage billing'}
                        </Button>
                    </div>

                    {!isActiveSubscription && !isTenantLoading && (
                        <div className="mt-4 space-y-2 border-t border-border pt-4">
                            <label
                                htmlFor="account-access-code"
                                className="text-xs uppercase tracking-[0.2em] text-muted-foreground"
                            >
                                Referral code (optional)
                            </label>
                            <Input
                                id="account-access-code"
                                value={accessCode}
                                onChange={(event) =>
                                    setAccessCode(event.target.value)
                                }
                                placeholder="Enter your access code"
                                autoComplete="off"
                                spellCheck={false}
                            />
                            <p className="text-xs text-muted-foreground">
                                Have a free access code? We&apos;ll apply it
                                when starting your subscription.
                            </p>
                            <div className="flex flex-wrap gap-2 pt-1">
                                <Button
                                    onClick={() => createBillingSession('checkout')}
                                    disabled={isBillingLoading}
                                >
                                    Start subscription
                                </Button>
                                <Button
                                    variant="ghost"
                                    onClick={checkSubscriptionStatus}
                                    disabled={isBillingLoading || isStatusChecking}
                                >
                                    {isStatusChecking
                                        ? 'Checking status…'
                                        : 'Refresh status'}
                                </Button>
                            </div>
                        </div>
                    )}

                    {billingStatus && (
                        <div
                            className={
                                statusTone === 'success'
                                    ? 'mt-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-500'
                                    : statusTone === 'warning'
                                      ? 'mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-600'
                                      : 'mt-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground'
                            }
                        >
                            {billingStatus}
                        </div>
                    )}
                    {billingError && (
                        <div className="mt-3 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                            {billingError}
                        </div>
                    )}
                </div>

                {/* Payment Methods card */}
                {(isBillingDetailsLoading ||
                    (billingDetails?.paymentMethods &&
                        billingDetails.paymentMethods.length > 0)) && (
                    <div className="mt-4 rounded-lg border border-border bg-card p-4 shadow-sm">
                        <h2 className="text-base font-semibold">
                            Payment Method
                        </h2>
                        <div className="mt-3 space-y-2">
                            {isBillingDetailsLoading ? (
                                <div className="text-sm text-muted-foreground">
                                    Loading…
                                </div>
                            ) : (
                                billingDetails?.paymentMethods.map((pm) => {
                                    const expLabel =
                                        pm.expMonth !== null &&
                                        pm.expYear !== null
                                            ? `Expires ${MONTH_NAMES[(pm.expMonth - 1) % 12]} ${pm.expYear}`
                                            : null;
                                    return (
                                        <div
                                            key={pm.id}
                                            className="flex items-center justify-between rounded-md border border-border px-3 py-3"
                                        >
                                            <div>
                                                <div className="flex items-center gap-2 text-sm font-medium">
                                                    {pm.name ?? 'Card'} ••••{' '}
                                                    {pm.last4}
                                                    {pm.isPrimary && (
                                                        <Badge className="rounded-full bg-primary text-primary-foreground">
                                                            Primary
                                                        </Badge>
                                                    )}
                                                </div>
                                                {expLabel && (
                                                    <div className="mt-0.5 text-xs text-muted-foreground">
                                                        {expLabel}
                                                    </div>
                                                )}
                                            </div>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="size-8 shrink-0"
                                                onClick={() =>
                                                    createBillingSession(
                                                        'portal',
                                                    )
                                                }
                                                disabled={isBillingLoading}
                                            >
                                                <Pencil className="size-3.5" />
                                            </Button>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                        <Button
                            variant="outline"
                            className="mt-2 w-full"
                            onClick={() => createBillingSession('portal')}
                            disabled={isBillingLoading}
                        >
                            <Plus className="size-4" />
                            Add payment method
                        </Button>
                    </div>
                )}

                {/* Transaction History card */}
                {(isBillingDetailsLoading ||
                    (billingDetails?.invoices &&
                        billingDetails.invoices.length > 0)) && (
                    <div className="mt-4 rounded-lg border border-border bg-card p-4 shadow-sm">
                        <h2 className="text-base font-semibold">
                            Transaction History
                        </h2>
                        {isBillingDetailsLoading ? (
                            <div className="mt-3 text-sm text-muted-foreground">
                                Loading…
                            </div>
                        ) : (
                            <div className="mt-3 overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-border text-left text-muted-foreground">
                                            <th className="pb-2 pr-4 font-normal">
                                                Reference
                                            </th>
                                            <th className="pb-2 pr-4 font-normal">
                                                Product
                                            </th>
                                            <th className="pb-2 pr-4 font-normal">
                                                Status
                                            </th>
                                            <th className="pb-2 pr-4 font-normal">
                                                Date
                                            </th>
                                            <th className="pb-2 text-right font-normal">
                                                Amount
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {billingDetails?.invoices.map((inv) => {
                                            const date = inv.date
                                                ? new Intl.DateTimeFormat(
                                                      'en-US',
                                                      { dateStyle: 'short' },
                                                  ).format(new Date(inv.date))
                                                : '—';
                                            const refNum = inv.number.includes(
                                                '-',
                                            )
                                                ? inv.number
                                                      .split('-')
                                                      .pop() ?? inv.number
                                                : inv.number;
                                            return (
                                                <tr
                                                    key={inv.id}
                                                    className="border-b border-border/50 last:border-0"
                                                >
                                                    <td className="py-3 pr-4 text-muted-foreground">
                                                        #{refNum}
                                                    </td>
                                                    <td className="py-3 pr-4 max-w-45 truncate">
                                                        {inv.description}
                                                    </td>
                                                    <td className="py-3 pr-4">
                                                        <InvoiceStatusBadge
                                                            status={inv.status}
                                                        />
                                                    </td>
                                                    <td className="py-3 pr-4 text-muted-foreground">
                                                        {date}
                                                    </td>
                                                    <td className="py-3 text-right">
                                                        {formatCurrency(
                                                            inv.amount,
                                                            inv.currency,
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
                {/* Tax Settings card */}
                <div className="mt-4 rounded-lg border border-border bg-card p-4 shadow-sm">
                    <h2 className="text-base font-semibold">Tax Settings</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Set your estimated income tax rate so Stockwright can show rough tax estimates on sales.
                    </p>
                    <div className="mt-4 space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="relative w-36">
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="1"
                                    placeholder="e.g. 22"
                                    value={taxRateInput}
                                    onChange={(e) => setTaxRateInput(e.target.value)}
                                    className="pr-8"
                                />
                                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                                    %
                                </span>
                            </div>
                            <Button
                                size="sm"
                                onClick={saveTaxRate}
                                disabled={isTaxSaving}
                            >
                                {isTaxSaving ? 'Saving…' : 'Save'}
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Enter your marginal federal income tax rate. Self-employment tax (15.3%) is always included in estimates as a separate line. These are estimates for planning purposes only — consult a tax professional for advice specific to your situation.
                        </p>
                    </div>
                </div>
            </div>
        </Layout>
    );
}